import { useEffect, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Typography, Box, Checkbox, Paper,
    List, ListItem, ListItemButton, ListItemIcon,
    ListItemText, Avatar, Chip, Stack, InputAdornment
} from "@mui/material";
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import SimpleBackdrop from "../../components/Loading/SimpleBackdrop";
import { AlertInfo } from "../../components/Alerts/Alert";
import { api } from "../../services/api";
import { UsuarioType } from "../../types/UsuarioType";
import { KanbanModulo } from "./TabKanbanModulos";

type Props = {
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
    modulo: KanbanModulo | null;
};

export default function ModalKanbanModulo({ open, onClose, onSaved, modulo }: Props) {
    const [openLoad, setOpenLoad] = useState(false);
    const [nomeModulo, setNomeModulo] = useState('');
    const [corHeader, setCorHeader] = useState('#000000');
    const [corCorpo, setCorCorpo] = useState('#FFFFFF');
    const [usuarios, setUsuarios] = useState<UsuarioType[]>([]);
    const [selecionados, setSelecionados] = useState<number[]>([]);
    const [busca, setBusca] = useState('');

    const isEdicao = modulo !== null;

    async function loadUsuarios() {
        try {
            const res = await api.get('/usuarios');
            if (res.data && !res.data.error) setUsuarios(res.data);
        } catch {
            console.log('Falha ao carregar usuários');
        }
    }

    useEffect(() => {
        if (open) {
            loadUsuarios();
            setBusca('');
            if (modulo) {
                setNomeModulo(modulo.nome_modulo);
                setCorHeader(modulo.cor_header || '#000000');
                setCorCorpo(modulo.cor_corpo || '#FFFFFF');
                setSelecionados(modulo.usuarios.map(u => u.id_usuario));
            } else {
                setNomeModulo('');
                setCorHeader('#000000');
                setCorCorpo('#FFFFFF');
                setSelecionados([]);
            }
        }
    }, [open, modulo]);

    function toggleUsuario(id: number) {
        setSelecionados(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    }

    async function handleSave() {
        if (!nomeModulo.trim())
            return AlertInfo({ icon: 'info', title: 'Informe o nome do módulo' });

        setOpenLoad(true);
        try {
            const body = { nome_modulo: nomeModulo.trim(), cor_header: corHeader, cor_corpo: corCorpo, usuarios_ids: selecionados };
            let res;

            if (isEdicao) {
                res = await api.put(`/updatekanbanmodulo/${modulo!.id_kanban_modulo}`, body);
            } else {
                res = await api.post('/createkanbanmodulo', body);
            }

            if (res.data.success) {
                AlertInfo({ icon: 'success', title: isEdicao ? 'Módulo atualizado!' : 'Módulo criado com sucesso!' });
                onSaved();
                onClose();
            } else {
                AlertInfo({ icon: 'error', title: res.data.message || 'Falha na operação' });
            }
        } catch {
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setOpenLoad(false);
    }

    const usuariosFiltrados = usuarios.filter(u =>
        u.nome_usuario.toLowerCase().includes(busca.toLowerCase())
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <SimpleBackdrop open={openLoad} />

            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isEdicao ? <EditIcon color="primary" /> : <ViewKanbanIcon color="primary" />}
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            {isEdicao ? 'Editar Módulo' : 'Novo Módulo Kanban'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {isEdicao ? 'Altere o nome e os participantes' : 'Defina o nome e selecione os participantes'}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <TextField
                    label="Nome do Módulo *"
                    fullWidth
                    size="small"
                    value={nomeModulo}
                    onChange={e => setNomeModulo(e.target.value)}
                    inputProps={{ maxLength: 255 }}
                    sx={{ mb: 2, mt: 0.5 }}
                />

                <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>
                    Cores do Módulo
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    Defina as cores que serão exibidas no board Kanban
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
                            Cor do Topo (Header)
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                                component="input"
                                type="color"
                                value={corHeader}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCorHeader(e.target.value)}
                                sx={{
                                    width: 40,
                                    height: 40,
                                    border: '1px solid #ccc',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    padding: 0,
                                    '&::-webkit-color-swatch-wrapper': { padding: 0 },
                                    '&::-webkit-color-swatch': { border: 'none', borderRadius: 1 },
                                }}
                            />
                            <TextField
                                size="small"
                                value={corHeader}
                                onChange={e => setCorHeader(e.target.value)}
                                inputProps={{ maxLength: 7 }}
                                sx={{ width: 100 }}
                            />
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
                            Cor do Corpo
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                                component="input"
                                type="color"
                                value={corCorpo}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCorCorpo(e.target.value)}
                                sx={{
                                    width: 40,
                                    height: 40,
                                    border: '1px solid #ccc',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    padding: 0,
                                    '&::-webkit-color-swatch-wrapper': { padding: 0 },
                                    '&::-webkit-color-swatch': { border: 'none', borderRadius: 1 },
                                }}
                            />
                            <TextField
                                size="small"
                                value={corCorpo}
                                onChange={e => setCorCorpo(e.target.value)}
                                inputProps={{ maxLength: 7 }}
                                sx={{ width: 100 }}
                            />
                        </Box>
                    </Box>
                </Box>

                <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>
                    Participantes
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    Selecione os usuários que terão acesso a este módulo
                </Typography>

                {selecionados.length > 0 && (
                    <Stack direction="row" flexWrap="wrap" gap={0.5} mb={1.5}>
                        {selecionados.map(id => {
                            const u = usuarios.find(u => u.id_usuario === id);
                            return u ? (
                                <Chip
                                    key={id}
                                    size="small"
                                    label={u.nome_usuario}
                                    onDelete={() => toggleUsuario(id)}
                                    avatar={<Avatar sx={{ width: 20, height: 20, fontSize: 10 }}>{u.nome_usuario.charAt(0)}</Avatar>}
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontSize: 12 }}
                                />
                            ) : null;
                        })}
                    </Stack>
                )}

                <TextField
                    fullWidth
                    size="small"
                    placeholder="Buscar usuário..."
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 1 }}
                />

                <Paper variant="outlined" sx={{ maxHeight: 250, overflow: 'auto', borderRadius: 1 }}>
                    <List dense disablePadding>
                        {usuariosFiltrados.length === 0 && (
                            <ListItem>
                                <ListItemText
                                    primary={<Typography variant="body2" color="text.disabled">Nenhum usuário encontrado</Typography>}
                                />
                            </ListItem>
                        )}
                        {usuariosFiltrados.map(u => {
                            const checked = selecionados.includes(u.id_usuario);
                            return (
                                <ListItem key={u.id_usuario} disablePadding>
                                    <ListItemButton onClick={() => toggleUsuario(u.id_usuario)} dense>
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <Checkbox
                                                edge="start"
                                                checked={checked}
                                                size="small"
                                                disableRipple
                                            />
                                        </ListItemIcon>
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>
                                                {u.nome_usuario.charAt(0)}
                                            </Avatar>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={u.nome_usuario}
                                            secondary={u.email_usuario}
                                            primaryTypographyProps={{ fontSize: 13, fontWeight: checked ? 'bold' : 'normal' }}
                                            secondaryTypographyProps={{ fontSize: 11 }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </Paper>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} color="inherit" variant="outlined">
                    Cancelar
                </Button>
                <Button onClick={handleSave} variant="contained" disableElevation>
                    {isEdicao ? 'Salvar Alterações' : 'Criar Módulo'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
